import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { spacing, colors } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useUpdateCustomMtaLabel } from 'api/ProjectsAPI';
import { EditableHeaderInput } from 'modules/roles/components/details/EditableHeaderInput';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { ApiError } from 'services/api';
import { SecondaryPageHeader } from 'ui/pageHeader';
import { Tag, TAG_SIZES } from 'ui/tags';
import { CUSTOM_EMAIL, SENDING_PLATFORMS_PAGE_TEXT } from '../../../constants';
import { useCustomSettings, useMetadata } from '../../../hooks';

export const HeaderContent = styled.div`
  display: grid;
  column-gap: ${spacing.half};
  grid-template-areas: 'logo name' 'logo tag';
  grid-template-columns: ${pxToRem(64)} 1fr;
  grid-template-rows: 1fr 1fr;
`;

export const LogoContainer = styled.div`
  align-items: center;
  background-color: ${colors.white};
  border: solid 1px ${colors.grey15};
  border-radius: ${pxToRem(4)};
  display: flex;
  grid-area: logo;
  height: ${pxToRem(64)};
  justify-content: center;
  width: ${pxToRem(64)};

  & img {
    width: 100%;
    padding: ${spacing.eighth};
  }
`;

export const CategoryTag = styled(Tag)`
  grid-area: tag;
  justify-self: start;
  margin: 0;
  padding-left: ${spacing.half};
  padding-right: ${spacing.half};
`;

export const CustomEmailPageHeader = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [headerText, setHeaderText] = useState('');
  const dispatch = useDispatch();
  const { customMTAESPLabel, logoUrl, refectCurrentProjectSettings } = useCustomSettings();
  const { mutate } = useUpdateCustomMtaLabel();

  const { name, category, logoSvg: Logo } = useMetadata(CUSTOM_EMAIL);

  useEffect(() => {
    setHeaderText(customMTAESPLabel || name);
  }, [customMTAESPLabel]);

  const handleUpdateHeader = () => {
    setHeaderText(headerText || customMTAESPLabel || name);
    setIsEditMode(false);

    if (headerText !== customMTAESPLabel && headerText !== '') {
      mutate(
        {
          customMTAESPLabel: headerText,
        },
        {
          onSuccess: () => {
            refectCurrentProjectSettings();
            dispatch(addPositiveToast(`Success! ${headerText} updated successfully`));
          },
          onError: (e: unknown) => {
            const { response } = e as ApiError;
            dispatch(addNegativeToast(response?.data));
          },
        },
      );
    }
  };

  const handleHeaderChange = (value: string) => {
    setHeaderText(value);
  };

  const toggleEditMode = () => {
    setIsEditMode(true);
  };

  return (
    <SecondaryPageHeader
      breadcrumb={{
        content: 'See all Sending Platforms',
        href: `/settings/sendingPlatforms`,
      }}
      data-test="sending-platforms-details-page-header"
      documentTitle={`${name} | ${SENDING_PLATFORMS_PAGE_TEXT.PAGE_HEADER}`}
    >
      <HeaderContent>
        <LogoContainer>
          {logoUrl ? <img src={logoUrl} alt="custom-logo" /> : <Logo height={48} width={48} />}
        </LogoContainer>
        <EditableHeaderInput
          isEditMode={isEditMode}
          onBlur={handleUpdateHeader}
          onChange={handleHeaderChange}
          onClick={toggleEditMode}
          value={headerText}
        />
        <CategoryTag size={TAG_SIZES.LARGE}>{category}</CategoryTag>
      </HeaderContent>
    </SecondaryPageHeader>
  );
};
